"use client";

import { useState } from "react";
import { Strategy } from "@/stores/strategiesStore";
import { PerformanceChart } from "@/components/strategies/PerformanceChart";
import { ContractBadge } from "@/components/strategies/ContractBadge";
import { OfferedBy } from "@/components/strategies/OfferedBy";
import { MarketViewBadge } from "./MarketViewBadge";
import { OwnerBadge } from "./OwnerBadge";
import { PriceBadge } from "./PriceBadge";
import { RiskBadge } from "./RiskBadge";
import { StrategyMetrics } from "./StrategyMetrics";
import { UserAllocationBadge } from "./UserAllocationBadge";
import { SHOW_STRATEGY_PERFORMANCE } from "@/utils/constants";

interface StrategyCardProps {
  strategy: Strategy;
  onAllocate: (strategyId: string) => void;
  onManage: (strategyId: string) => void;
  isViewOnly?: boolean;
}

export const StrategyCard = ({
  strategy,
  onAllocate,
  onManage,
  isViewOnly = false,
}: StrategyCardProps) => {
  const hasAllocation = strategy.userAllocation !== null;
  const [showChart, setShowChart] = useState(false);

  return (
    <div className="rounded-xl border border-slate-200/60 bg-white/5 p-5 transition hover:border-slate-200/80">
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <MarketViewBadge view={strategy.marketView} />
          {strategy.ownerType === "third_party" && <OwnerBadge />}
          <h3 className="text-lg font-semibold text-white">{strategy.name}</h3>
        </div>
        <div className="flex items-center gap-2">
          <PriceBadge resolvedPrice={strategy.resolvedPrice} />
          <RiskBadge level={strategy.riskLevel} />
        </div>
      </div>

      {/* Description */}
      <p className="mb-3 line-clamp-2 text-sm text-slate-400">{strategy.description}</p>

      {/* Third-party attribution (SFWL-010 / RTI-002) — a binding disclosure
          requirement, not a performance number. Always visible for
          third-party strategies regardless of SHOW_STRATEGY_PERFORMANCE or
          whether the (now-gated) performance chart is expanded — previously
          this only rendered inside the expanded chart area, which meant it
          could be missed entirely. */}
      {strategy.ownerType === "third_party" && (
        <OfferedBy
          ownerName={strategy.ownerName}
          ownerLogoUrl={strategy.ownerLogoUrl}
          disclosureUrl={strategy.disclosureUrl}
          className="mb-4"
        />
      )}

      {/* Contracts */}
      {strategy.supportedAssets?.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
            Contracts
          </span>
          {strategy.supportedAssets.map((asset) => (
            <ContractBadge key={asset} asset={asset} />
          ))}
        </div>
      )}

      {/* Metrics Grid */}
      <StrategyMetrics strategy={strategy} />

      {/* Performance Chart Toggle — Issue #131: the chart renders the same
          disputed cumulative-return series pending an exporter fix +
          restatement, so the toggle (and therefore the chart panel it
          reveals) is gated behind SHOW_STRATEGY_PERFORMANCE. PerformanceChart
          itself is untouched — gated at this call site only. */}
      {SHOW_STRATEGY_PERFORMANCE && (
        <button
          onClick={() => setShowChart(!showChart)}
          className="mt-3 text-xs font-medium text-slate-400 transition hover:text-blue-400"
        >
          {showChart ? "Hide Performance" : "View Performance"}
        </button>
      )}

      {SHOW_STRATEGY_PERFORMANCE && showChart && (
        <PerformanceChart
          strategyId={strategy.id}
          strategyName={strategy.name}
          className="mt-3"
        />
      )}

      {/* User Allocation (if any) */}
      {hasAllocation && <UserAllocationBadge allocation={strategy.userAllocation!} />}

      {/* Actions */}
      {!isViewOnly && (
        <div className="mt-4 flex gap-2">
          {hasAllocation ? (
            <>
              <button
                onClick={() => onManage(strategy.id)}
                className="flex-1 rounded-lg border border-slate-600 py-2 text-sm font-medium text-slate-300 transition hover:bg-slate-800 hover:text-white"
              >
                Manage
              </button>
              <button
                onClick={() => onAllocate(strategy.id)}
                className="flex-1 rounded-lg bg-blue-500 py-2 text-sm font-semibold text-white transition hover:bg-blue-600"
              >
                + Add More
              </button>
            </>
          ) : (
            <button
              onClick={() => onAllocate(strategy.id)}
              className="w-full rounded-lg bg-blue-500 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-600"
            >
              Allocate
            </button>
          )}
        </div>
      )}
    </div>
  );
};
